import React, { ReactNode, useMemo } from 'react';
import { Provider, useSelector, useStore } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import { IAllStates } from '../../../store/rootReducer';
import { ICampaignOverview } from '../../../model/briefing/overview/ICampaignOverview';
import { IBrandContractStore, contractBrand, setContractModalDetails } from '../../../store/brand/contractBrandSlice';
import {
    IContractInfluencer,
    contractInfluencer,
    setContractModalDetails as setInfluencerContractModalDetails,
} from '../../../store/influencer/contractInfluencerSlice';
import {
    IContractAgency,
    contractAgency,
    setOfferModalDetails as setAgencyOfferModalDetails,
} from '../../../store/agency/agencyContractSlice';

interface IAnnexBaseTabProps {
    side: 'brand' | 'influencer' | 'agency';
    children: ReactNode;
}

/**
 * Shows the contract an amendment replaces with the same components as the Amendment tab. The children get a frozen
 * copy of the store in which that contract is the loaded one, so nothing they dispatch reaches the real store.
 */
export const AnnexBaseTab = ({ side, children }: IAnnexBaseTabProps) => {
    const mainStore = useStore<IAllStates>();
    const campaign = useSelector((state: IAllStates) =>
        side === 'brand'
            ? state.contractBrand.campaign
            : side === 'agency'
            ? state.contractAgency.campaign
            : state.contractInfluencer.campaign
    );

    const frozenStore = useMemo(() => {
        const current = mainStore.getState();
        if (!campaign?.annexBase) {
            return null;
        }
        const baseCampaign: ICampaignOverview = {
            ...campaign,
            contracts: [campaign.annexBase.contract],
            contractPlaceholder: campaign.annexBase.contractPlaceholder,
        };
        const frozenState: IAllStates = { ...current };
        if (side === 'brand') {
            frozenState.contractBrand = {
                ...contractBrand.reducer(current.contractBrand, setContractModalDetails(baseCampaign)),
                contractModalStatus: 'preview',
                isAnnexMode: false,
                isEditable: false,
                canCounter: false,
            } as IBrandContractStore;
        } else if (side === 'agency') {
            frozenState.contractAgency = {
                ...contractAgency.reducer(current.contractAgency, setAgencyOfferModalDetails(baseCampaign)),
                contractModalStatus: 'preview',
                isEditable: false,
                canCounter: false,
            } as IContractAgency;
        } else {
            frozenState.contractInfluencer = {
                ...contractInfluencer.reducer(
                    current.contractInfluencer,
                    setInfluencerContractModalDetails(baseCampaign)
                ),
                contractModalStatus: 'preview',
                isEditable: false,
                canCounter: false,
            } as IContractInfluencer;
        }
        return configureStore({ reducer: () => frozenState, middleware: [], devTools: false });
    }, [mainStore, campaign, side]);

    if (!frozenStore) {
        return <>{children}</>;
    }
    return <Provider store={frozenStore}>{children}</Provider>;
};
